import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  Radio,
  Button,
  Row,
  Col,
  Space,
  Typography,
  Divider,
  InputNumber,
  message,
  Upload,
  Spin,
} from 'antd';
import { UploadOutlined, DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import { GameProfileQuestion } from '../../../types/gameProfileQuestion';
import { GameProfileQuestionSet } from '../../../types/gameProfileQuestionSet';
import { GAME_PROFILE } from '../../../constants/define/translation';
import {
  SingleColumnAnswers,
  TwoColumnAnswers,
  DropdownWithSearchAnswers,
  SearchAnswers,
  ImageTileAnswers,
} from './answerForm';
import { QuestionSetModal } from './QuestionSetModal';
import { getGameProfileQuestionSetsRepository } from '../../../repository/mytm/gameProfile/gameProfileQuestionSetsRepository';
import { getGameProfileQuestionDetailRepository } from '../../../repository/mytm/gameProfile/gameProfileQuestionsRepository';
import { logging } from '../../../utils/log';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

interface QuestionFormProps {
  questionId?: string | null;
  onSave: (values: any) => void;
  loading: boolean;
  onCancel?: () => void;
  readOnly?: boolean;
  country?: string | null;
}

interface AnswerOption {
  id?: string;
  title: string;
  subTitle?: string;
  imageLink?: string;
}

const QuestionForm: React.FC<QuestionFormProps> = ({
  questionId,
  onSave,
  loading,
  onCancel,
  readOnly = false,
  country,
}) => {
  const { t } = useTranslation(GAME_PROFILE);
  const [form] = Form.useForm();
  const [answerType, setAnswerType] = useState<string>('SINGLE_ONE_COLUMN');
  const [answerOptions, setAnswerOptions] = useState<AnswerOption[]>([]);
  const [numberOfAnswers, setNumberOfAnswers] = useState<number>(2);
  const [charCounts, setCharCounts] = useState({
    subCategory: 0,
    sectionName: 0,
    name: 0,
  });
  const [questionType, setQuestionType] = useState<string>('SINGLE');
  const [questionSets, setQuestionSets] = useState<GameProfileQuestionSet[]>([]);
  const [questionSetModalVisible, setQuestionSetModalVisible] = useState(false);
  const [questionSetLoading, setQuestionSetLoading] = useState(false);
  const [questionData, setQuestionData] = useState<GameProfileQuestion | null>(null);
  const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);

  // Clear multipleAnswersLimit when switching to SINGLE
  useEffect(() => {
    if (questionType === 'SINGLE') {
      form.setFieldsValue({ multipleAnswersLimit: null });
    }
  }, [questionType, form]);

  const answerTypeOptions = [
    { value: 'SINGLE_ONE_COLUMN', label: t('questionForm.answerTypes.singleOneColumn') },
    { value: 'SINGLE_TWO_COLUMNS', label: t('questionForm.answerTypes.singleTwoColumns') },
    { value: 'DROPDOWN_WITH_SEARCH', label: t('questionForm.answerTypes.dropdownWithSearch') },
    { value: 'SEARCH', label: t('questionForm.answerTypes.search') },
    { value: 'IMAGE_TILE', label: t('questionForm.answerTypes.imageTile') },
  ];

  const questionCategories = [
    { value: 'MY_GAME', label: t('questionForm.categories.myGame') },
    { value: 'MY_EQUIPMENT', label: t('questionForm.categories.myEquipment') },
    { value: 'MY_TEAMS', label: t('questionForm.categories.myTeams') },
  ];

  const questionTypes = [
    { value: 'SINGLE', label: t('questionForm.questionTypes.single') },
    {
      value: 'MULTIPLE',
      label: t('questionForm.questionTypes.multiple'),
      disabled: answerType === 'DROPDOWN_WITH_SEARCH' || answerType === 'SEARCH',
    },
  ];

  const loadQuestionSets = async () => {
    try {
      setQuestionSetLoading(true);
      const data = await getGameProfileQuestionSetsRepository(country);
      setQuestionSets(data || []);
    } catch (error) {
      logging(error);
      message.error(t('questionForm.messages.loadQuestionSetsFailed'));
    } finally {
      setQuestionSetLoading(false);
    }
  };

  useEffect(() => {
    loadQuestionSets();
  }, []);

  const handleOpenQuestionSetModal = () => setQuestionSetModalVisible(true);

  const handleCloseQuestionSetModal = () => setQuestionSetModalVisible(false);

  const handleQuestionSetCreated = async () => {
    try {
      // Reload question sets to get the latest list
      await loadQuestionSets();

      // The newly created question set should be at the top of the list (newest first)
      // We'll set it after the list is reloaded
      const data = await getGameProfileQuestionSetsRepository(country || 'USA');
      if (data && data.length > 0) {
        // Assume the newest one is first in the list
        form.setFieldsValue({ gameProfileGroupSetId: data[0].id });
        setQuestionSets(data);
      }
    } catch (error) {
      logging(error);
    }
    setQuestionSetModalVisible(false);
  };

  const generateOptionId = (): string => `${Date.now()}_${Math.random().toString(36).slice(2)}`;

  const handleCharCountChange = (field: keyof typeof charCounts, value: string) => {
    setCharCounts(prev => ({
      ...prev,
      [field]: value.length,
    }));
  };

  const getTemplateFileName = (type: string): string => {
    switch (type) {
      case 'SINGLE_ONE_COLUMN':
        return 'single-one-column.csv';
      case 'SINGLE_TWO_COLUMNS':
        return 'single-two-columns.csv';
      case 'DROPDOWN_WITH_SEARCH':
        return 'dropdown-with-search.csv';
      case 'SEARCH':
        return 'search.csv';
      case 'IMAGE_TILE':
        return 'image-tile.csv';
      default:
        return 'single-one-column.csv';
    }
  };

  const handleDownloadTemplate = () => {
    const fileName = getTemplateFileName(answerType);
    const templatePath = `/templates/${fileName}`;

    // Create a temporary link element to trigger download
    const link = document.createElement('a');
    link.href = templatePath;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success(t('questionForm.messages.templateDownloaded'));
  };

  const isSupportedAnswerType = (type: string): boolean =>
    ['SINGLE_ONE_COLUMN', 'SINGLE_TWO_COLUMNS', 'DROPDOWN_WITH_SEARCH', 'SEARCH', 'IMAGE_TILE'].includes(type);

  const detectAnswerTypeFromCsv = (csvData: any[], currentType: string): string => {
    if (!csvData.length) return currentType;
    const firstRow = csvData[0] as any;
    if (Object.prototype.hasOwnProperty.call(firstRow, 'answerType') && isSupportedAnswerType(firstRow.answerType)) {
      return String(firstRow.answerType);
    }
    return currentType;
  };

  const handleUploadCsv = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: results => {
        try {
          const csvData = results.data as any[];

          if (csvData.length === 0) {
            message.error(t('questionForm.messages.emptyCsvFile'));
            return;
          }

          // Detect answer type from CSV (fallback to current if missing)
          const detectedType = detectAnswerTypeFromCsv(csvData, answerType);

          // If CSV answer type differs, switch the form/state to match CSV
          if (detectedType !== answerType) {
            setAnswerType(detectedType);
            form.setFieldsValue({ answerType: detectedType });
            // Force SINGLE for types that require single select
            if (detectedType === 'DROPDOWN_WITH_SEARCH' || detectedType === 'SEARCH') {
              form.setFieldsValue({ questionType: 'SINGLE' });
            }
          }

          // Validate CSV structure based on detected answer type
          const isValidStructure = validateCsvStructure(csvData, detectedType);
          if (!isValidStructure) {
            message.error(t('questionForm.messages.invalidCsvStructure'));
            return;
          }

          // Convert CSV data to answer options
          const newAnswerOptions = csvData.map((row, index) => ({
            id: generateOptionId(),
            title: row.title || '',
            subTitle: row.subTitle || '',
            imageLink: row.imageLink || '',
          }));

          // Validate minimum answers requirement
          const minRequired = getMinAnswersForValidation(detectedType);
          if (newAnswerOptions.length < minRequired) {
            message.error(t('questionForm.messages.atLeastAnswersRequired', { count: minRequired }));
            return;
          }

          // Update answer options and number of answers
          setAnswerOptions(newAnswerOptions);
          setNumberOfAnswers(newAnswerOptions.length);

          message.success(t('questionForm.messages.csvUploadedSuccessfully'));
        } catch (error) {
          message.error(t('questionForm.messages.csvParseError'));
        }
      },
      error: error => {
        message.error(t('questionForm.messages.csvParseError'));
      },
    });

    return false; // Prevent default upload behavior
  };

  const validateCsvStructure = (csvData: any[], currentAnswerType: string): boolean => {
    if (csvData.length === 0) return false;

    const firstRow = csvData[0];

    // Determine which type to validate columns against
    const effectiveType =
      Object.prototype.hasOwnProperty.call(firstRow, 'answerType') && isSupportedAnswerType(firstRow.answerType)
        ? String(firstRow.answerType)
        : currentAnswerType;

    // Then check other required columns based on effective answer type
    switch (effectiveType) {
      case 'SINGLE_ONE_COLUMN':
      case 'SINGLE_TWO_COLUMNS':
      case 'DROPDOWN_WITH_SEARCH':
        return Object.prototype.hasOwnProperty.call(firstRow, 'title');
      case 'SEARCH':
        return (
          Object.prototype.hasOwnProperty.call(firstRow, 'title') &&
          Object.prototype.hasOwnProperty.call(firstRow, 'subTitle') &&
          Object.prototype.hasOwnProperty.call(firstRow, 'imageLink')
        );
      case 'IMAGE_TILE':
        return (
          Object.prototype.hasOwnProperty.call(firstRow, 'title') &&
          Object.prototype.hasOwnProperty.call(firstRow, 'imageLink')
        );
      default:
        return false;
    }
  };

  // Fetch question data when questionId changes
  useEffect(() => {
    const fetchQuestionData = async () => {
      if (!questionId) {
        setQuestionData(null);
        return;
      }

      try {
        setIsLoadingQuestion(true);
        const data = await getGameProfileQuestionDetailRepository(questionId);
        setQuestionData(data);
      } catch (error) {
        logging(error);
        message.error('Failed to load question details');
        setQuestionData(null);
      } finally {
        setIsLoadingQuestion(false);
      }
    };

    fetchQuestionData();
  }, [questionId]);

  useEffect(() => {
    if (questionData) {
      let parsedOptions: any = {};
      if (questionData.options) {
        try {
          parsedOptions = JSON.parse(questionData.options);
        } catch (e) {
          parsedOptions = {};
        }
      }
      form.setFieldsValue({
        name: questionData.name,
        category: questionData.category,
        klaviyoPropertyName: parsedOptions?.klaviyoName || '',
        questionType: questionData.questionType,
        subCategory: questionData.subCategory,
        sectionName: questionData.sectionName,
        answerType: questionData.answerType,
        multipleAnswersLimit: parsedOptions?.multipleAnswersLimit || null,
        gameProfileGroupSetId: questionData.gameProfileGroupSetId || questionData.questionSet?.id || null,
      });
      setAnswerType(questionData.answerType);
      setQuestionType(questionData.questionType || 'SINGLE');

      // Initialize character counts
      setCharCounts({
        subCategory: questionData.subCategory?.length || 0,
        sectionName: questionData.sectionName?.length || 0,
        name: questionData.name?.length || 0,
      });

      // Initialize answer options - use max of default count or existing answers count
      const defaultCount = getMinAnswers(questionData.answerType);
      const existingAnswersCount = questionData.answers?.length || 0;
      const actualCount = Math.max(defaultCount, existingAnswersCount);

      const initialOptions = Array.from({ length: actualCount }, (_, index) => {
        const existingAnswer = questionData.answers && questionData.answers[index];
        return {
          id: generateOptionId(),
          title: existingAnswer?.title || '',
          subTitle: existingAnswer?.subTitle || '',
          imageLink: existingAnswer?.imageLink || '',
        };
      });
      setAnswerOptions(initialOptions);
      setNumberOfAnswers(actualCount);
    } else if (!questionId) {
      form.setFieldsValue({
        category: 'MY_GAME',
        questionType: 'SINGLE',
        answerType: 'SINGLE_ONE_COLUMN',
        gameProfileGroupSetId: null,
      });
      setQuestionType('SINGLE');
      initializeAnswerOptions('SINGLE_ONE_COLUMN');
    }
  }, [questionData, questionId, form]);

  const initializeAnswerOptions = (type: string) => {
    const minAnswers = getMinAnswers(type);
    const defaultCount = type === 'SINGLE_ONE_COLUMN' ? 12 : minAnswers;
    const initialOptions = Array.from({ length: defaultCount }, (_, index) => ({
      id: generateOptionId(),
      title: '',
      subTitle: '',
      imageLink: '',
    }));
    setAnswerOptions(initialOptions);
    setNumberOfAnswers(defaultCount);
  };

  // For generating UI input fields
  const getMinAnswers = (type: string): number => {
    switch (type) {
      case 'SINGLE_ONE_COLUMN':
        return 12;
      case 'SINGLE_TWO_COLUMNS':
        return 22;
      case 'IMAGE_TILE':
        return 12;
      case 'DROPDOWN_WITH_SEARCH':
        return 2;
      case 'SEARCH':
        return 2;
      default:
        return 2;
    }
  };

  // For validation when submitting
  const getMinAnswersForValidation = (type: string): number => {
    switch (type) {
      case 'SINGLE_ONE_COLUMN':
        return 2;
      case 'SINGLE_TWO_COLUMNS':
        return 10;
      case 'IMAGE_TILE':
        return 2;
      case 'DROPDOWN_WITH_SEARCH':
        return 2;
      case 'SEARCH':
        return 2;
      default:
        return 2;
    }
  };

  const getMaxAnswers = (type: string): number => {
    switch (type) {
      case 'SINGLE_ONE_COLUMN':
        return 12;
      case 'IMAGE_TILE':
        return 12;
      case 'SINGLE_TWO_COLUMNS':
        return 22;
      case 'DROPDOWN_WITH_SEARCH':
        return 50;
      case 'SEARCH':
        return 50;
      default:
        return 12;
    }
  };

  const handleAnswerTypeChange = (value: string) => {
    setAnswerType(value);
    initializeAnswerOptions(value);

    // Auto switch to SINGLE when selecting DROPDOWN_WITH_SEARCH or SEARCH
    if (value === 'DROPDOWN_WITH_SEARCH' || value === 'SEARCH') {
      form.setFieldsValue({ questionType: 'SINGLE' });
    }
  };

  const handleNumberOfAnswersChange = (value: number | null) => {
    if (value && value >= getMinAnswers(answerType) && value <= getMaxAnswers(answerType)) {
      setNumberOfAnswers(value);
      generateAnswerOptions(value);
    }
  };

  const generateAnswerOptions = (count: number) => {
    const newOptions = Array.from({ length: count }, (_, index) => {
      const existingOption = answerOptions[index];
      return (
        (existingOption && { ...existingOption, id: existingOption.id || generateOptionId() }) || {
          id: generateOptionId(),
          title: '',
          subTitle: '',
          imageLink: '',
        }
      );
    });
    setAnswerOptions(newOptions);
  };

  const resetAnswerList = () => {
    const resetCount = getMinAnswers(answerType); // default back to minimum (2 for SEARCH/DROPDOWN)
    const emptyOptions = Array.from({ length: resetCount }, () => ({
      id: generateOptionId(),
      title: '',
      subTitle: '',
      imageLink: '',
    }));
    setAnswerOptions(emptyOptions);
    setNumberOfAnswers(resetCount);
  };

  const handleAnswerOptionChange = (index: number, field: keyof AnswerOption, value: string) => {
    const newOptions = [...answerOptions];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setAnswerOptions(newOptions);
  };

  // Calculate number of valid answers (answers with non-empty title)
  const getValidAnswersCount = (): number => {
    return answerOptions.filter(option => option.title.trim() !== '').length;
  };

  const addAnswerOption = () => {
    if (answerOptions.length < getMaxAnswers(answerType)) {
      const newOption = {
        id: generateOptionId(),
        title: '',
        subTitle: '',
        imageLink: '',
      };
      setAnswerOptions([...answerOptions, newOption]);
      setNumberOfAnswers(answerOptions.length + 1);
    }
  };

  const removeAnswerOption = (index: number) => {
    if (answerOptions.length > getMinAnswersForValidation(answerType)) {
      const newOptions = answerOptions.filter((_, i) => i !== index);
      setAnswerOptions(newOptions);
      setNumberOfAnswers(newOptions.length);
    }
  };

  const handleSubmit = (values: any) => {
    // Validate form first
    form
      .validateFields()
      .then(() => {
        // Filter out empty answer options
        const validAnswers = answerOptions.filter(option => option.title.trim() !== '');
        const minRequired = getMinAnswersForValidation(answerType);

        if (validAnswers.length < minRequired) {
          message.error(t('questionForm.messages.atLeastAnswersRequired', { count: minRequired }));
          return;
        }

        // Validate multipleAnswersLimit if questionType is MULTIPLE
        if (
          values.questionType === 'MULTIPLE' &&
          values.multipleAnswersLimit !== null &&
          values.multipleAnswersLimit !== undefined
        ) {
          if (values.multipleAnswersLimit > validAnswers.length) {
            message.error(
              t('questionForm.messages.multipleAnswersLimitExceeded', { count: validAnswers.length }) ||
                `Multiple answers limit cannot exceed ${validAnswers.length} (number of valid answers)`,
            );
            return;
          }
        }

        // Filter answers based on answerType requirements
        const filteredAnswers = validAnswers.map(option => {
          const baseAnswer = { title: option.title };

          switch (answerType) {
            case 'SINGLE_ONE_COLUMN':
            case 'SINGLE_TWO_COLUMNS':
              return baseAnswer;
            case 'DROPDOWN_WITH_SEARCH':
              return baseAnswer;
            case 'SEARCH':
              return {
                ...baseAnswer,
                subTitle: option.subTitle || '',
                imageLink: option.imageLink || '',
              };
            case 'IMAGE_TILE':
              return {
                ...baseAnswer,
                imageLink: option.imageLink || '',
              };
            default:
              return baseAnswer;
          }
        });

        // Remove multipleAnswersLimit and klaviyoPropertyName from top level, they should only be in options
        const { multipleAnswersLimit, klaviyoPropertyName, ...formValues } = values;

        const formData = {
          ...formValues,
          options: {
            nameColor: '#000',
            ...(values.multipleAnswersLimit !== null && values.multipleAnswersLimit !== undefined
              ? { multipleAnswersLimit: String(values.multipleAnswersLimit) }
              : {}),
            ...(klaviyoPropertyName ? { klaviyoName: klaviyoPropertyName.trim() } : {}),
          },
          answers: filteredAnswers,
        };

        onSave(formData);
      })
      .catch(errorInfo => {});
  };

  const renderAnswerInputs = () => {
    switch (answerType) {
      case 'SINGLE_ONE_COLUMN':
        return (
          <SingleColumnAnswers
            t={t}
            answerOptions={answerOptions}
            onOptionChange={handleAnswerOptionChange}
            readOnly={readOnly}
          />
        );
      case 'SINGLE_TWO_COLUMNS':
        return (
          <TwoColumnAnswers
            t={t}
            answerOptions={answerOptions}
            onOptionChange={handleAnswerOptionChange}
            readOnly={readOnly}
          />
        );
      case 'DROPDOWN_WITH_SEARCH':
        return (
          <DropdownWithSearchAnswers
            t={t}
            answerOptions={answerOptions}
            onOptionChange={handleAnswerOptionChange}
            onAddOption={addAnswerOption}
            maxAnswers={getMaxAnswers(answerType)}
            readOnly={readOnly}
          />
        );
      case 'SEARCH':
        return (
          <SearchAnswers
            t={t}
            answerOptions={answerOptions}
            onOptionChange={handleAnswerOptionChange}
            onAddOption={addAnswerOption}
            onRemoveOption={removeAnswerOption}
            minAnswers={getMinAnswers(answerType)}
            maxAnswers={getMaxAnswers(answerType)}
            readOnly={readOnly}
          />
        );
      case 'IMAGE_TILE':
        return (
          <ImageTileAnswers
            t={t}
            answerOptions={answerOptions}
            onOptionChange={handleAnswerOptionChange}
            readOnly={readOnly}
          />
        );
      default:
        return (
          <SingleColumnAnswers
            t={t}
            answerOptions={answerOptions}
            onOptionChange={handleAnswerOptionChange}
            readOnly={readOnly}
          />
        );
    }
  };

  return (
    <StyledFormContainer>
      <Spin spinning={isLoadingQuestion}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <StyledFormSection>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="category"
                  label={t('questionForm.labels.questionCategory')}
                  rules={[{ required: true, message: t('questionForm.validation.selectCategory') }]}
                >
                  <Radio.Group disabled={readOnly}>
                    {questionCategories.map(category => (
                      <Radio key={category.value} value={category.value}>
                        {category.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  name="questionType"
                  label={t('questionForm.labels.questionType')}
                  rules={[{ required: true, message: t('questionForm.validation.selectQuestionType') }]}
                >
                  <Radio.Group
                    disabled={readOnly}
                    onChange={e => {
                      setQuestionType(e.target.value);
                    }}
                  >
                    {questionTypes.map(type => (
                      <Radio key={type.value} value={type.value} disabled={type.disabled || readOnly}>
                        {type.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
                {questionType === 'MULTIPLE' && answerType !== 'SEARCH' && answerType !== 'DROPDOWN_WITH_SEARCH' && (
                  <Form.Item
                    name="multipleAnswersLimit"
                    label={t('questionForm.labels.multipleAnswersLimit')}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (value === null || value === undefined || value === '') {
                            return Promise.resolve();
                          }
                          const validAnswersCount = getValidAnswersCount();
                          if (value > validAnswersCount) {
                            return Promise.reject(
                              new Error(
                                t('questionForm.validation.multipleAnswersLimitExceeded', {
                                  count: validAnswersCount,
                                }) || `Maximum value cannot exceed ${validAnswersCount} (number of valid answers)`,
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      max={getValidAnswersCount() > 0 ? getValidAnswersCount() : undefined}
                      placeholder={t('questionForm.placeholders.multipleAnswersLimit')}
                      style={{ width: '100%' }}
                      disabled={readOnly}
                    />
                  </Form.Item>
                )}
              </Col>
              <Col span={12}>
                <Form.Item
                  name="gameProfileGroupSetId"
                  label={t('questionForm.labels.questionSet')}
                  rules={[{ required: true, message: t('questionForm.validation.selectQuestionSet') }]}
                >
                  <Select
                    placeholder={t('questionForm.placeholders.selectQuestionSet')}
                    loading={questionSetLoading}
                    disabled={readOnly}
                    dropdownStyle={{ borderRadius: '6px' }}
                    dropdownRender={menu => (
                      <>
                        {!readOnly && (
                          <>
                            <Button
                              type="text"
                              icon={<PlusOutlined />}
                              onClick={handleOpenQuestionSetModal}
                              style={{
                                width: '100%',
                                textAlign: 'left',
                                padding: '8px',
                                fontWeight: 700,
                                fontSize: '12px',
                              }}
                            >
                              Add new
                            </Button>
                            <Divider style={{ margin: '2px' }} />
                          </>
                        )}
                        {menu}
                      </>
                    )}
                  >
                    {questionSets.map(set => (
                      <Option key={set.id} value={set.id}>
                        {set.groupSetName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="klaviyoPropertyName"
                  label={t('questionForm.labels.klaviyoPropertyName')}
                  rules={[
                    { required: true, message: t('questionForm.validation.inputKlaviyoPropertyName') },
                    { max: 64, message: t('questionForm.validation.maxKlaviyoPropertyName') },
                  ]}
                >
                  <Input placeholder={t('questionForm.placeholders.klaviyoPropertyName')} disabled={readOnly} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name="subCategory" label={t('questionForm.labels.subcategory')}>
                  <Input
                    placeholder={t('questionForm.placeholders.subcategoryInput')}
                    maxLength={50}
                    onChange={e => handleCharCountChange('subCategory', e.target.value)}
                    disabled={readOnly}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="sectionName"
                  label={t('questionForm.labels.sectionName')}
                  rules={[{ required: true, message: t('questionForm.validation.inputSectionName') }]}
                >
                  <Input
                    placeholder={t('questionForm.placeholders.nameInput')}
                    maxLength={50}
                    onChange={e => handleCharCountChange('sectionName', e.target.value)}
                    disabled={readOnly}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label={t('questionForm.labels.questionPrompt')}
                  rules={[{ required: true, message: t('questionForm.validation.inputQuestionPrompt') }]}
                >
                  <TextArea
                    rows={1}
                    placeholder={t('questionForm.placeholders.questionPrompt')}
                    maxLength={78}
                    onChange={e => handleCharCountChange('name', e.target.value)}
                    disabled={readOnly}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="answerType"
                  label={t('questionForm.labels.answerType')}
                  rules={[{ required: true, message: t('questionForm.validation.selectAnswerType') }]}
                >
                  <Select
                    placeholder={t('questionForm.placeholders.selectAnswerType')}
                    onChange={handleAnswerTypeChange}
                    value={answerType}
                    disabled={readOnly}
                  >
                    {answerTypeOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {(answerType === 'DROPDOWN_WITH_SEARCH' || answerType === 'SEARCH') && (
              <StyledTopControls>
                <Form.Item label={t('questionForm.labels.numberOfAnswers')}>
                  <StyledNumAnswersBox>
                    <InputNumber
                      min={getMinAnswers(answerType)}
                      max={getMaxAnswers(answerType)}
                      value={numberOfAnswers}
                      onChange={handleNumberOfAnswersChange}
                      className="num-input"
                      disabled={readOnly}
                    />
                    <Button
                      className="generate-btn"
                      onClick={() => generateAnswerOptions(numberOfAnswers)}
                      disabled={readOnly}
                    >
                      {t('questionForm.buttons.generate')}
                    </Button>
                  </StyledNumAnswersBox>
                  <Button type="link" className="reset-list-link" onClick={resetAnswerList} disabled={readOnly}>
                    Reset List
                  </Button>
                </Form.Item>

                {!readOnly && (
                  <StyledCsvSection>
                    <Space size={32}>
                      <Upload accept=".csv" beforeUpload={handleUploadCsv} showUploadList={false}>
                        <Button className="csv-upload-btn" icon={<UploadOutlined />}>
                          {t('questionForm.buttons.uploadCsv')}
                        </Button>
                      </Upload>
                      <Button
                        className="csv-download-link"
                        type="link"
                        icon={<DownloadOutlined />}
                        onClick={handleDownloadTemplate}
                      >
                        {t('questionForm.buttons.downloadTemplate')}
                      </Button>
                    </Space>
                  </StyledCsvSection>
                )}
              </StyledTopControls>
            )}

            {!(answerType === 'DROPDOWN_WITH_SEARCH' || answerType === 'SEARCH') && !readOnly && (
              <StyledCsvSection>
                <Space size={32}>
                  <Upload accept=".csv" beforeUpload={handleUploadCsv} showUploadList={false}>
                    <Button className="csv-upload-btn" icon={<UploadOutlined />}>
                      {t('questionForm.buttons.uploadCsv')}
                    </Button>
                  </Upload>
                  <Button
                    className="csv-download-link"
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadTemplate}
                  >
                    {t('questionForm.buttons.downloadTemplate')}
                  </Button>
                </Space>
              </StyledCsvSection>
            )}
          </StyledFormSection>

          {renderAnswerInputs()}

          {!readOnly && (
            <StyledFormActions>
              <StyledSectionHeader>
                <Text type="secondary">{t('questionForm.messages.answersLeftEmpty')}</Text>
              </StyledSectionHeader>
              <Space>
                <Button className="btn-save" type="primary" htmlType="submit" loading={loading}>
                  {questionId ? t('questionForm.buttons.updateQuestion') : t('questionForm.buttons.addQuestion')}
                </Button>
              </Space>
            </StyledFormActions>
          )}
        </Form>
      </Spin>
      <QuestionSetModal
        visible={questionSetModalVisible}
        onCancel={handleCloseQuestionSetModal}
        onSuccess={handleQuestionSetCreated}
        country={country}
      />
    </StyledFormContainer>
  );
};

const StyledFormContainer = styled.div`
  padding: 24px;

  .ant-form-item-label > label {
    text-align: left;
    font-weight: 700;
    font-size: 14px;
  }

  .ant-input,
  .ant-select-selector,
  .ant-input-number,
  .ant-radio-group {
    border-radius: 8px !important;
  }

  .ant-divider-horizontal {
    margin: 1px 0;
  }

  .position-relative {
    position: relative;
  }

  .count-item-title {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11px;
    color: #cbcbcb;
    pointer-events: none;
  }

  textarea {
    padding-right: 20px;
  }
`;

const StyledFormSection = styled.div`
  margin-bottom: 24px;
`;

const StyledSectionHeader = styled.div`
  margin-bottom: 16px;

  .ant-typography {
    display: block;
    margin-bottom: 4px;
  }
`;

const StyledCsvSection = styled.div`
  margin: 16px 0;
  display: flex;
  justify-content: flex-end;

  .csv-upload-btn {
    height: 48px;
    padding: 0 24px;
    border-radius: 10px;
    border: 1px solid #000;
    background: #fff;
    color: #000;
    font-weight: 600;
    margin-left: 10px;
  }

  .csv-upload-btn .anticon {
    margin-right: 12px;
  }

  .csv-download-link {
    height: 48px;
    display: inline-flex;
    align-items: center;
    padding: 0;
    font-weight: 600;
    color: #000;
    text-decoration: underline;
  }
`;

const StyledTopControls = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 8px;

  .reset-list-link span {
    font-weight: 600;
    color: #000;
    text-decoration: underline;
    height: 40px;
  }
`;

const StyledNumAnswersBox = styled.div`
  display: inline-flex;
  align-items: center;
  height: 48px;
  border: 1px solid #d9d9d9;
  border-radius: 10px;
  padding: 0 1px 0 10px;
  background: #fff;

  .num-input {
    border: none !important;
    box-shadow: none !important;
  }

  .generate-btn {
    height: 40px;
    margin-left: 1px;
    font-weight: 700;
    border: none;
  }
`;

const StyledFormActions = styled.div`
  display: flex;
  justify-content: space-between;
  text-align: right;
  margin-top: 24px;
  padding-top: 16px;

  .btn-save {
    background-color: black;
    border-radius: 8px;
    border: 1px solid #000;
    font-weight: 600;
    height: 40px;
    text-transform: uppercase;
  }
`;

export default QuestionForm;
